import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  editProfilePicture,
  removeProfilePicture,
} from '../../../api/users-api';
import { User } from '../../../../types';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import ProfilePicUploadDialog from './profile-pic-upload-dialog';

import { useTheme } from '@mui/material/styles';

interface EditProfilePicProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  user: User;
  changeLogRoles: string[];
}

export default function EditProfilePicDialog({
  open,
  handleClose,
  userEmail,
  user,
  changeLogRoles,
}: EditProfilePicProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const { mutate, isPending: isEditing } = useMutation({
    mutationFn: (file: File) =>
      editProfilePicture(userEmail, file, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User profile picture updated successfully!',
      });
      setChangeLogMessage('');
      const userWithoutPic = {
        ...user,
        thumbnailPhotoUrl: '',
      };
      queryClient.setQueryData([UserKeys.User, userEmail], userWithoutPic);
      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserPhoto, userEmail],
      });
      setPendingFile(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          `user's profile picture`
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.update_profile_pic, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.userDetails,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: deleteMutate, isPending: isDeleting } = useMutation({
    mutationFn: () => removeProfilePicture(userEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User profile picture deleted successfully',
      });
      setChangeLogMessage('');
      const userWithoutPic = {
        ...user,
        thumbnailPhotoUrl: null,
      };
      queryClient.setQueryData([UserKeys.User, userEmail], userWithoutPic);
      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserPhoto, userEmail],
      });
      setPendingFile(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          `user's profile picture`
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.delete_profile_pic);
      setOpenChangeLogDialog(false);
    },
  });

  function requiresChangeLog() {
    return isAbleToSeeUIElement(
      changeLogRoles,
      DirectoryRoles.ChangeProfilePicture.value
    );
  }

  function handleUploadSubmit(file: File) {
    if (requiresChangeLog()) {
      setPendingFile(file);
      setOpenChangeLogDialog(true);
    } else {
      mutate(file);
    }
  }

  function handleDeleteSubmit() {
    setPendingFile(null);
    if (requiresChangeLog()) {
      setOpenChangeLogDialog(true);
    } else {
      deleteMutate();
    }
  }

  const isLoading = isEditing || isDeleting;

  return (
    <ProfilePicUploadDialog
      open={open}
      handleClose={handleClose}
      title="Edit Profile Picture"
      onSave={handleUploadSubmit}
      isUploading={isEditing}
      isLoading={isLoading}
      secondaryButton={
        user.thumbnailPhotoUrl !== null
          ? {
              loading: isLoading,
              disabled: isLoading,
              action: handleDeleteSubmit,
              title: 'Delete',
              sx: {
                textTransform: 'none',
                mb: 2,
                mr: 1,
                color: theme.palette.semantic.button.secondary.text,
                borderColor: theme.palette.semantic.button.secondary.border,
              },
            }
          : undefined
      }
    >
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (pendingFile) {
            mutate(pendingFile);
          } else {
            deleteMutate();
          }
        }}
        mutateLoading={isLoading}
      />
    </ProfilePicUploadDialog>
  );
}
